import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeRegistrationLocale = (language) => {
  const raw = (language || '').toLowerCase();
  if (raw.startsWith('pt')) return 'pt-BR';
  if (raw.startsWith('es')) return 'es-CO';
  if (raw.startsWith('en')) return 'en-US';
  return 'en-US';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const normalizeUser = (userData) => {
    return {
      ...userData,
      name: userData.full_name || userData.name || 'Usuario',
      role: userData.role || 'user',
      is_verified: userData.is_verified !== undefined ? userData.is_verified : true
    };
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(normalizeUser(response.data));
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    const normalizedUser = normalizeUser(userData);
    setUser(normalizedUser);
    return normalizedUser;
  };

  const register = async (email, password, name, invitationCode = null, language = null) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      invitation_code: invitationCode,
      locale: normalizeRegistrationLocale(language || (typeof navigator !== 'undefined' ? navigator.language : null))
    });
    return response.data;
  };

  const verifyEmail = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-email`, { email, code });
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (email, code, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { 
      email, 
      code, 
      new_password: newPassword 
    });
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const updatePreferences = async (prefs) => {
    const response = await axios.patch(
      `${API}/auth/me/preferences`,
      prefs,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(normalizeUser(response.data));
    return response.data;
  };

  const logout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAdmin: user?.role === 'admin',
    currency: user?.currency || 'BRL',
    locale: user?.locale || 'en-US',
    login,
    register,
    verifyEmail,
    forgotPassword,
    resetPassword,
    changePassword,
    updatePreferences,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
